import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../../components/common/Modal';
import { PermissionGrid } from './PermissionGrid';
import {
  useRoleCatalog,
  useRoleDetail,
  useCreateRole,
  useUpdateRoleMetadata,
  useReplaceRolePermissions,
  useResetRoleDefaults,
} from '../../../hooks/useRoles';
import type { Permission, PermissionAction } from '../../../types/role';
import { useLanguage } from '../../../features/language/useLanguage';
import { usePermissions } from '../../../hooks/usePermissions';

interface RoleEditorModalProps {
  isOpen: boolean;
  roleId: string | null;          // null → create mode
  onClose: () => void;
  onSaved: () => void;
}

export const RoleEditorModal: React.FC<RoleEditorModalProps> = ({ isOpen, roleId, onClose, onSaved }) => {
  const { t } = useLanguage();
  const { canEditRoles } = usePermissions();
  const { data: catalog = [] } = useRoleCatalog();
  // machine_types is hidden from the grid — it's coupled to `machines` via the
  // dependency cascade below, so the user only sees a single "Machine" row and
  // any toggle on it propagates to machine_types automatically.
  const visibleCatalog = useMemo(
    () => catalog.filter((r) => r.key !== 'machine_types'),
    [catalog],
  );
  const { data: detail, isFetching } = useRoleDetail(roleId || undefined);

  const [displayName, setDisplayName] = useState('');
  const [description, setDescription] = useState('');
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    // Re-seed form state from `detail` whenever the modal opens or the role
    // being edited changes. setState here is intentional (mirroring external
    // prop into form state) — disable the rule for this block.
    /* eslint-disable react-hooks/set-state-in-effect */
    setError(null);
    if (detail) {
      setDisplayName(detail.display_name);
      setDescription(detail.description || '');
      setPermissions(detail.permissions ?? []);
    } else {
      setDisplayName('');
      setDescription('');
      setPermissions([]);
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [isOpen, detail]);

  const createMut = useCreateRole();
  const updateMetaMut = useUpdateRoleMetadata(roleId || '');
  const replacePermsMut = useReplaceRolePermissions(roleId || '');
  const resetMut = useResetRoleDefaults(roleId || '');

  const readOnly = !canEditRoles || detail?.is_system === true;
  const isDefaultEditable =
    detail && !detail.is_system && (detail.name === 'staff' || detail.name === 'viewer');

  const dependencies: Record<string, Permission[]> = {
    'purchase_orders:write': [
      { obj: 'suppliers', act: 'read' },
      { obj: 'suppliers', act: 'write' },
      { obj: 'inventory', act: 'read' },
      { obj: 'inventory', act: 'write' },
      { obj: 'work_orders.roasting', act: 'read' },
    ],
    // Spec 100: write on roasting WOs implies the same operational deps as
    // the legacy work_orders key. Packing write does NOT imply recipe access
    // (packing has no recipe concept) — no dependency entry needed.
    'work_orders.roasting:write': [
      { obj: 'recipes', act: 'read' },
      { obj: 'recipes', act: 'write' },
      { obj: 'machines', act: 'read' },
      { obj: 'machine_types', act: 'read' },
      { obj: 'roasters', act: 'read' },
      { obj: 'artisan_profiles', act: 'read' },
      { obj: 'inventory', act: 'read' },
      { obj: 'inventory', act: 'write' },
      { obj: 'roast_profiles', act: 'write' }
    ],
    'machine_types:read': [{ obj: 'machines', act: 'read' }],
    'machine_types:write': [
      { obj: 'machines', act: 'read' },
      { obj: 'machines', act: 'write' },
    ],
    'machines:read': [{ obj: 'machine_types', act: 'read' }],
    'machines:write': [
      { obj: 'machine_types', act: 'read' },
      { obj: 'machine_types', act: 'write' },
    ],
    'sales_orders:write': [
      { obj: 'customers', act: 'read' },
      { obj: 'customers', act: 'write' },
      { obj: 'inventory', act: 'read' },
      { obj: 'inventory', act: 'write' },
      { obj: 'company', act: 'read' },
    ],
    'cupping_sessions:write': [
      { obj: 'inventory', act: 'read' },
      { obj: 'inventory', act: 'write' },
      { obj: 'taste_profiles', act: 'write' },
    ],
  };

  const handlePermissionChange = (next: Permission[]) => {
    const has = (list: Permission[], obj: string, act: string) =>
      list.some((p) => p.obj === obj && p.act === act);
    const supports = (obj: string, act: PermissionAction) =>
      catalog.find((r) => r.key === obj)?.actions.includes(act) ?? false;

    let updated = [...next];

    for (const [key, deps] of Object.entries(dependencies)) {
      const [keyObj, keyAct] = key.split(':');
      const keyAdded = has(next, keyObj, keyAct) && !has(permissions, keyObj, keyAct);
      if (keyAdded) {
        for (const dep of deps) {
          if (!has(updated, dep.obj, dep.act)) updated.push(dep);
        }
      }
      const dependencyRemoved = deps.some(
        (dep) => has(permissions, dep.obj, dep.act) && !has(next, dep.obj, dep.act),
      );
      if (dependencyRemoved && has(updated, keyObj, keyAct)) {
        updated = updated.filter((p) => !(p.obj === keyObj && p.act === keyAct));
      }
    }

    // Universal: write implies read. Granting (X, write) auto-grants (X, read);
    // removing (X, read) auto-revokes (X, write). Read remains independent.
    // Guarded by `supports` so resources whose catalog omits `read` (e.g.
    // green_bean_profiles — read is universal there) don't get an invalid pair.
    const writesAdded = updated.filter(
      (p) => p.act === 'write' && !has(permissions, p.obj, 'write'),
    );
    for (const w of writesAdded) {
      if (supports(w.obj, 'read') && !has(updated, w.obj, 'read')) {
        updated.push({ obj: w.obj, act: 'read' });
      }
    }
    const readsRemoved = permissions.filter(
      (p) => p.act === 'read' && !has(next, p.obj, 'read'),
    );
    for (const r of readsRemoved) {
      updated = updated.filter((p) => !(p.obj === r.obj && p.act === 'write'));
    }

    setPermissions(updated);
  };

  const handleSave = async () => {
    setError(null);
    try {
      if (!roleId) {
        await createMut.mutateAsync({ display_name: displayName, description, permissions });
      } else {
        await updateMetaMut.mutateAsync({ display_name: displayName, description });
        await replacePermsMut.mutateAsync(permissions);
      }
      onSaved();
      onClose();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || t('roles.editor.saveFailed', undefined, 'Failed to save role'));
    }
  };

  const handleReset = async () => {
    setResetConfirmOpen(false);
    setError(null);
    try {
      await resetMut.mutateAsync();
      onSaved();
      onClose();
    } catch (err) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(msg || t('roles.editor.resetFailed', undefined, 'Failed to reset role'));
    }
  };

  const title = !roleId
    ? t('roles.editor.createTitle', undefined, 'Create Role')
    : readOnly
      ? t('roles.editor.viewTitle', undefined, 'View Role')
      : t('roles.editor.editTitle', undefined, 'Edit Role');

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} maxWidth="5xl">
      {!canEditRoles && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md text-amber-800 text-sm">
          {t('roles.editor.upgradeBanner', undefined, 'Upgrade your plan to customize roles.')}
        </div>
      )}
      {isFetching && <div className="p-4 text-center text-gray-500">{t('common.loading', undefined, 'Loading…')}</div>}

      <fieldset disabled={readOnly} className={readOnly ? 'opacity-80' : undefined}>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('roles.editor.displayName', undefined, 'Role Name')}
            </label>
            <input
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder={t(
                'roles.editor.displayNamePlaceholder',
                undefined,
                'e.g. Warehouse Supervisor',
              )}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              data-test-id="role-editor-display-name"
              required
              maxLength={100}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              {t('roles.editor.description', undefined, 'Description')}
            </label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t(
                'roles.editor.descriptionPlaceholder',
                undefined,
                'What is this role responsible for?',
              )}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm border p-2"
              data-test-id="role-editor-description"
            />
          </div>
          {detail?.is_system ? (
            <div className="p-3 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-600">
              {t(
                'roles.editor.systemRoleNote',
                undefined,
                'System roles have full access to every resource. Permissions cannot be edited.',
              )}
            </div>
          ) : (
            <PermissionGrid catalog={visibleCatalog} value={permissions} onChange={handlePermissionChange} disabled={readOnly} />
          )}
        </div>
      </fieldset>

      {error && <p className="mt-3 text-sm text-red-600">{error}</p>}

      <div className="mt-6 flex flex-col-reverse sm:flex-row sm:justify-between sm:items-center gap-3 pt-4 border-t">
        <div className="flex">
          {isDefaultEditable && !readOnly && (
            <button
              type="button"
              onClick={() => setResetConfirmOpen(true)}
              data-test-id="role-editor-reset-defaults"
              className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-amber-700 border border-amber-300 rounded-md hover:bg-amber-50"
            >
              {t('roles.editor.resetDefaults', undefined, 'Reset to defaults')}
            </button>
          )}
        </div>
        <div className="flex gap-3 sm:ml-auto">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            {t('common.cancel', undefined, 'Cancel')}
          </button>
          {!readOnly && (
            <button
              type="button"
              onClick={handleSave}
              disabled={!displayName || createMut.isPending || updateMetaMut.isPending || replacePermsMut.isPending}
              data-test-id="role-editor-save"
              className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50"
            >
              {t('common.save', undefined, 'Save')}
            </button>
          )}
        </div>
      </div>

      <Modal
        isOpen={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
        title={t('roles.editor.resetConfirmTitle', undefined, 'Reset to default permissions?')}
        maxWidth="sm"
        zIndexClass="z-[60]"
      >
        <p className="text-sm text-gray-600">
          {t('roles.editor.resetConfirmBody', undefined, 'This replaces all current permissions with the canonical defaults. Continue?')}
        </p>
        <div className="mt-5 flex justify-end gap-3">
          <button type="button" onClick={() => setResetConfirmOpen(false)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
            {t('common.cancel', undefined, 'Cancel')}
          </button>
          <button type="button" onClick={handleReset} className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700">
            {t('roles.editor.resetConfirm', undefined, 'Reset')}
          </button>
        </div>
      </Modal>
    </Modal>
  );
};
